import { useLocation, useNavigate, Link } from "react-router-dom";
import { smoothScrollToTop } from "../../helpers/scroll-to-top";
import { useState, useEffect, useRef } from 'react';
import { FaFacebookF, FaClock, FaEnvelope, FaChevronDown, FaPhone } from 'react-icons/fa';
import styles from './navbar.module.css'

import { logPhoneCallClick, logFacebookClick } from "../../helpers/google-analytics/gaEvents";

export default function Navbar() {
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showHours, setShowHours] = useState(true);
  const lastScrollY = useRef(0);
  const scrollThreshold = useRef(0);
  const isMobile = window.innerWidth <= 950;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = Math.abs(currentScrollY - lastScrollY.current);

      // Only update if scrolled at least 30px to avoid flickering
      if (scrollDelta >= 100) {
        if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
          // Scrolling down - hide hours
          setShowHours(false);
          scrollThreshold.current = currentScrollY;
        } else if (currentScrollY < lastScrollY.current && currentScrollY < scrollThreshold.current - 30) {
          // Scrolling up with enough distance - show hours
          setShowHours(true);
          scrollThreshold.current = currentScrollY;
        }
        lastScrollY.current = currentScrollY;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle dropdown on mobile (tap)
  const toggleDropdown = () => setShowDropdown((v) => !v);

  // Close dropdown and mobile menu when clicking a link
  const closeMenus = () => {
    setShowDropdown(false);
    setMobileMenuOpen(false);
  };

  const navigate = useNavigate();
  const handleLogoClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();       // Prevent normal navigation
      smoothScrollToTop(800);   // Scroll smoothly to top
      closeMenus();
    } else {
      closeMenus();
      navigate("/");            // Navigate to home if not already there
    }
  };

  //Styles
  const hamburgerMenu = `${styles.hamburger} ${mobileMenuOpen ? styles.active : ''}`
  const navLinks = `${styles.navLinks} ${mobileMenuOpen ? styles.open : ''}`
  const desktopDropdown = `${styles.dropdown} ${showDropdown ? styles.open : ''} ${styles.desktopDropdown}`
  const dropdownToggle = `${styles.dropdownToggle} ${location.pathname.includes('/services') ? styles.active : ''}`

  return (
    <>
      <div className={styles.headerTopbar}>
        <a
          href="https://www.facebook.com/1stopkitchens"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.socialIcon}
          aria-label="Facebook"
          onClick={logFacebookClick}
        >
          <FaFacebookF />
        </a>
        {!isMobile &&
          <p>
            📞 (506) 458-8801
          </p>
        }

        <a href="/#/contact-us" className={styles.contactLink} aria-label="Email address">
          ✉️ 1stopkitchens@nb.aibn.com
        </a>
      </div>
      <header className={`${styles.navbar} ${!showHours ? styles.compact : ''}`}>
        <div className={`container ${styles.navbarInner}`}>
          <div className={styles.mainNav}>
            <div className={styles.mobileHeader}>
              {/* Hamburger on the left */}
              <button
                className={hamburgerMenu}
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              >
                <span className={styles.hamburgerLine} />
                <span className={styles.hamburgerLine} />
                <span className={styles.hamburgerLine} />
              </button>

              {/* Logo in the center */}
              <Link to="/" className={styles.logo} onClick={handleLogoClick}>
                <img src="https://ik.imagekit.io/1stopkitchens/images/logo.png" alt="Artisan Kitchens" className={styles.logoImg} />
              </Link>

              {/* Email button on the right */}
              {isMobile && 
                <a href="/#/contact-us" className={styles.emailIcon} aria-label="Email address">
                  <FaEnvelope size={24}/>
                </a>}
            </div>

            {/* Sidebar nav */}
            <nav className={navLinks}>
              <Link to="/" className={location.pathname === '/' ? styles.active : ''} onClick={closeMenus}>
                Home
              </Link>

              <div className={desktopDropdown}>
                <span
                  className={dropdownToggle}
                  onClick={toggleDropdown}
                  tabIndex={0}
                  role="button"
                  aria-haspopup="true"
                  aria-expanded={showDropdown}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleDropdown();
                    }
                  }}
                >
                  Services <FaChevronDown className={styles.dropdownIcon} size={16} />
                </span>
                <div className={styles.dropdownMenu}>
                  <Link to="/services" className={location.pathname === '/services' ? styles.active : ''} onClick={closeMenus}>
                    All Services
                  </Link>
                  <Link
                    to="/services/countertops"
                    className={location.pathname === '/services/countertops' ? styles.active : ''}
                    onClick={closeMenus}
                  >
                    Countertops
                  </Link>
                  <Link
                    to="/services/kitchens"
                    className={location.pathname === '/services/kitchens' ? styles.active : ''}
                    onClick={closeMenus}
                  >
                    Kitchens
                  </Link>
                  <Link
                    to="/services/bathrooms"
                    className={location.pathname === '/services/bathrooms' ? styles.active : ''}
                    onClick={closeMenus}
                  >
                    Bathrooms
                  </Link>
                  <Link
                    to="/services/fixtures"
                    className={location.pathname === '/services/fixtures' ? styles.active : ''}
                    onClick={closeMenus}
                  >
                    Sinks & Faucets
                  </Link>
                </div>
              </div>

              <Link to="/about" className={location.pathname === '/about' ? styles.active : ''} onClick={closeMenus}>
                About
              </Link>
              <Link to="/contact-us" className={location.pathname === '/contact-us' ? styles.active : ''} onClick={closeMenus}>
                Contact
              </Link>
                
              {mobileMenuOpen && 
              <div className={styles.phoneIcon}>
                Tap to Call Us Today!
                <a aria-label="Phone number" href="tel:+15064588801" title="Click to call" onClick={()=> logPhoneCallClick()}>
                  <FaPhone size={24} />
                </a>
              </div>}
            </nav>

            {/* Optional backdrop for sidebar */}
            {mobileMenuOpen && <div className={styles.backdrop} onClick={closeMenus} />}
          </div>

          <div className={styles.headerRight}>
            <div className={`${styles.businessHours} ${showHours ? '' : styles.hidden}`}>
              <h4>Showroom Hours</h4>
              <p><FaClock /> Mon–Fri: 8am – 5pm</p>
              <p><FaClock /> Sat–Sun: Closed</p>
              <i>*After hours consultations available upon request</i>
            </div>

            {/* <div className={styles.socialIcons}>
              <a
                href="https://www.facebook.com/1stopkitchens"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialIcon}
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>
            </div> */}
          </div>
        </div>
      </header>
    </>
  );

}